import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Machine} from '../../../model/machine';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MachineGroup} from "../../../model/machine-group";
import {MachineGroupsService} from "../../../services/machine-groups.service";
import {MachineGroupPersistRequest} from "../../../model/requests/machine-group-persist-request";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {MachinesService} from "../../../services/machines.service";
import {MachineModelsService} from "../../../services/machine-models.service";
import {MachineModel} from "../../../model/machine-model";
import {AppConfig} from "../../../shared/global-variables";

@Component({
  selector: 'app-machine-groups-form',
  templateUrl: './machine-groups-form.component.html',
  styleUrls: ['./machine-groups-form.component.scss']
})
export class MachineGroupsFormComponent implements OnInit, ComponentCanDeactivate {

  public mode: 'create' | 'edit';

  public machineGroupForm: FormGroup;
  public persistingChanges: boolean;
  public areMachineGroupsLoading: boolean = true;
  public loadedData: boolean;

  public availableMachines: Machine[] = [];
  public targetMachines: Machine[] = [];

  public modelMap: Map<string, MachineModel>;

  private models: MachineModel[];
  private allMachines: Machine[] = [];

  private color: string;
  private machineGroup: MachineGroup;
  private formSuccess: boolean;


  constructor(private machineGroupsService: MachineGroupsService,
              private machinesService: MachinesService,
              private machineModelsService: MachineModelsService,
              private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private commonMessagesService: CommonMessagesService,
              private router: Router) {
    this.availableMachines = [];
    this.targetMachines = [];
  }

  ngOnInit(): void {
    this.loadDataAndForm();
  }

  private loadDataAndForm(): void {
    this.areMachineGroupsLoading = true;
    this.loadedData = false;
    this.modelMap = new Map<string, MachineModel>();
    const promises = [
      this.machineModelsService.getModelsSummary().toPromise().then(response => {
        this.models = response.models;
        for (const model of this.models) {
          this.modelMap.set(model.machineModelUuid, model);
        }
      }),
      this.machinesService.getMachinesSummary().toPromise().then(response => {
        this.allMachines = response.machines;
      })
    ];

    Promise.all(promises).then((): void => {
      this.route.url.subscribe(urlSegment => {
        const firstPath = urlSegment[0].path;
        if (firstPath === 'new') {
          this.mode = 'create';
          this.loadCreationForm();
        } else {
          this.mode = 'edit';
          this.loadEditionForm(firstPath);
        }
      });
    }).finally(() => this.areMachineGroupsLoading = false);
  }

  loadCreationForm(): void {
    this.machineGroup = new MachineGroup();
    this.machineGroup.color = '#2196F3';
    this.availableMachines = this.allMachines;
    this.createForm(this.machineGroup);
  }

  loadEditionForm(machineGroupUuid: string): void {
    this.machineGroupsService.getMachineGroupByUuid(machineGroupUuid).subscribe(
      response => {
        this.machineGroup = response.machineGroup;

        if (this.machineGroup.machines != null && this.machineGroup.machines.length > 0) {
          let targetMachinesUuids = this.machineGroup.machines.map(machine => machine.machineUuid);
          this.availableMachines = this.allMachines.filter(machine => !targetMachinesUuids.includes(machine.machineUuid));
          this.targetMachines = this.allMachines.filter(machine => targetMachinesUuids.includes(machine.machineUuid));
        } else {
          this.availableMachines = this.allMachines;
        }

        this.createForm(this.machineGroup);
        this.machineGroupForm.addControl('uuid', new FormControl(this.machineGroup.machineGroupUuid,
          [Validators.required, Validators.maxLength(36)]));
      }, error => {
        if (error.status == 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private createForm(machineGroup: MachineGroup): void {
    this.loadedData = true;
    this.color = machineGroup.color;
    this.machineGroupForm = this.formBuilder.group({
      name: [machineGroup.name, [Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255), Validators.required]],
      color: [machineGroup.color, [Validators.required]],
      description: [machineGroup.description, [Validators.maxLength(255)]],
      machineGroupUuid: this.mode === 'edit' ? this.machineGroup.machineGroupUuid : null
    });
    this.machineGroupForm.markAsPending();
  }

  persistMachineGroupData(): void {
    this.persistingChanges = true;
    const persistData: MachineGroupPersistRequest = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.machineGroupsService.createGroup(persistData).toPromise();
    } else {
      promise = this.machineGroupsService.editGroup(persistData).toPromise();
    }

    promise.then((): void => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate(['/groups']);

    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): MachineGroupPersistRequest {
    const formValue = this.machineGroupForm.getRawValue();
    return {
      name: formValue.name?.trim(),
      color: formValue.color,
      description: formValue.description?.trim(),
      machineUuids: this.getTargetMachinesUuids(),
      machineGroupUuid: this.mode === 'edit' ? this.machineGroup.machineGroupUuid : null
    };
  }

  private getTargetMachinesUuids(): string[] {
    return this.targetMachines?.length > 0 ? this.targetMachines.map(machine => machine.machineUuid) : [];
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.machineGroupForm?.untouched && this.machinesUntouched());
  }

  machinesUntouched(): boolean {
    if (this.mode === 'create') {
      return this.targetMachines?.length === 0;
    } else {
      let newMachinesUuids: string[] = this.getTargetMachinesUuids().sort((a, b) => a.localeCompare(b));
      let oldMachinesUuids: string[] = this.machineGroup.machines ?
        this.machineGroup.machines.map(machine => machine.machineUuid).sort((a, b) => a.localeCompare(b)) : [];
      return JSON.stringify([...new Set(oldMachinesUuids)])
        === JSON.stringify([...new Set(newMachinesUuids)]);
    }
  }

}
